import authentication from "@irati/auth"
import {Business, User, DealsPackage, ProductPermissions} from "@irati/types"
import firstnameLastname from "@irati/identity/helpers/user/firstnameLastname"
import getDeviceLocale from "@irati/utils/lang/helpers/getDeviceLocale"
import domain from "../env/domain"
import env from "../env"
import API from "../services/API"
import intercomScript, {getIntercomKey, getIntercomBaseUrl} from "./script"
import BaseIntercom, {dealPackageToString, packageAddons, productAccessData} from "./base"
import intercomTokenService from "./services/token"
import channel from "./helpers/channel"

class Intercom extends BaseIntercom {
  protected setup() {
    const shouldLoad = !env.IS_LOCAL && (domain.isTenant() || domain.isGlobal() || domain.isMerchant())
    if (shouldLoad) {
      intercomScript(getIntercomKey())
    }
  }

  protected async setIdentity(user: User, business: Business, dealPackage: DealsPackage, productPermissions?: ProductPermissions) {
    if (!window.Intercom) {
      return notLoaded()
    }

    const versionData = await this.getClientVersion()

    let userHash = null
    if (API.hasTokens() && !authentication.isUserGhost()) {
      userHash = await intercomTokenService.get(channel)
    }

    window.Intercom("shutdown")
    return window.Intercom("boot", {
      ...defaultIntercomSettings(),
      ...identityData(user, business, dealPackage, productPermissions),
      ...versionData,
      user_hash: userHash
    })
  }

  protected async updateIdentity(user: User, business: Business, dealPackage?: DealsPackage, productPermissions?: ProductPermissions) {
    if (window.Intercom) {
      const versionData = await this.getClientVersion()

      let userHash = null
      if (API.hasTokens() && !authentication.isUserGhost()) {
        userHash = await intercomTokenService.get(channel)
      }

      window.Intercom("update", {
        ...identityData(user, business, dealPackage, productPermissions),
        ...versionData,
        user_hash: userHash
      })
    }
  }

  protected clearIdentity() {
    if (window.Intercom) {
      window.Intercom("shutdown")
      window.Intercom("boot", {
        ...defaultIntercomSettings()
      })
    }
  }

  hide() {
    if (window.Intercom) {
      window.Intercom("update", {
        hide_default_launcher: true
      })
    }
  }

  show(msg?: string) {
    if (window.Intercom) {
      // Ensure the default launcher is visible
      window.Intercom("update", {
        hide_default_launcher: false
      })
      // Explicitly open the chat window
      if (msg) {
        window.Intercom("showNewMessage", msg)
      } else {
        window.Intercom("show")
      }
    }
  }

  logEvent(eventName: string, data: any) {
    if (!window.Intercom) {
      return notLoaded()
    }
    return window.Intercom("trackEvent", eventName, data)
  }

  addCustomMessage(message: string, url?: string) {
    if (window.Intercom) {
      window.Intercom("showNewMessage", message)
      if (url) {
        window.Intercom("update", {Url: url})
      }
    }
  }

  startTour(tourId: number) {
    if (window.Intercom) {
      window.Intercom("startTour", tourId)
    }
  }
}

export default new Intercom()

let thrown = false

function notLoaded() {
  if (!thrown) {
    // only throw once
    thrown = true
    // As 3rd party script, cannot always guarantee it will load
    console.error("Intercom failed to load")
  }

  return thrown
}

function defaultIntercomSettings() {
  return {
    api_base: getIntercomBaseUrl(),
    app_id: getIntercomKey()
  }
}

function identityData(user: User, business: Business, dealPackage?: DealsPackage, productPermissions?: ProductPermissions) {
  const role = user.role

  return {
    user_id: user.uuid,
    email: user.email,
    name: firstnameLastname(user),
    role,
    region: dealPackage?.region,
    device_locale: getDeviceLocale(),
    language_override: authentication.userLocale(),
    company: {
      company_id: business.uuid,
      name: business.businessName,
      tenant_group_id: business.groupIds,
      user_locale: user.language,
      tenant_uuid: business.uuid
    },
    monthlyTTValue: dealPackage?.monthlyTTValue,
    dealPackage: dealPackageToString(dealPackage),
    addons_enabled: packageAddons(dealPackage),
    product_access: productAccessData(productPermissions)
  }
}
